import { useAppSelector } from 'state/store'
import useWorkflow from './useWorkflow'
import { useTranslation } from 'react-i18n-lite'
import { useCallback } from 'react'
import { ExecutionState } from 'types/workflow'
import { ActivityValue, NestableGroupValue } from 'types/shared'
import useAuthentication from 'authentication/useAuthentication'

const useInterpolateUrl = () => {
  const { t, language } = useTranslation()

  const { token } = useAuthentication()

  const workflowUuid = useAppSelector(
    (state) => state.interactionModel.workflowUuid
  )
  const { workflow } = useWorkflow({ workflowUuid, t })

  const flatData = useAppSelector((state) => state.interactionModel.flatData)

  const interpolateUrl = useCallback(
    (url: string): string => {
      if (!url) return ''

      let interpolatedUrl = url

      if (language) {
        interpolatedUrl = interpolatedUrl.replace(/\{\{LANGUAGE\}\}/g, language)
      }

      if (token) {
        interpolatedUrl = interpolatedUrl.replace(/\{\{TOKEN\}\}/g, token)
      }

      if (workflow?.uuid) {
        interpolatedUrl = interpolatedUrl.replace(
          /\{\{PROCESS_UUID\}\}/g,
          workflow.uuid
        )
        interpolatedUrl = interpolatedUrl.replace(
          /\{\{WORKFLOW_UUID\}\}/g,
          workflow.uuid
        )
      }

      if (flatData?.uuid) {
        interpolatedUrl = interpolatedUrl.replace(
          /\{\{INTERACTION_UUID\}\}/g,
          flatData.uuid
        )
      }

      if (flatData?.activity_values) {
        interpolatedUrl = interpolatedUrl.replace(
          /\{\{activity-(.*?)\}\}/g,
          (_match, activityModelUuid) => {
            const activityValue = flatData.activity_values[
              activityModelUuid
            ] as ActivityValue | NestableGroupValue | undefined

            if (typeof activityValue === 'string') return activityValue

            if (Array.isArray(activityValue))
              return JSON.stringify(activityValue)

            if (
              activityValue &&
              typeof activityValue === 'object' &&
              !Array.isArray(activityValue)
            ) {
              const obj = activityValue as { id?: string; des?: string }
              if (obj.id && typeof obj.id === 'string') return obj.id
              if (obj.des && typeof obj.des === 'string') return obj.des
              return JSON.stringify(obj)
            }

            return ''
          }
        )
      }

      if (workflow?.execution_state) {
        try {
          const executionState = JSON.parse(
            workflow.execution_state
          ) as ExecutionState
          if (executionState?.data_container) {
            const dc = executionState.data_container as
              | Record<string, string>
              | undefined
            interpolatedUrl = interpolatedUrl.replace(
              /\{\{process-data-(.*?)\}\}/g,
              (_m, dataContainerKey) => {
                const dataContainerVariable = dc?.[dataContainerKey]
                return dataContainerVariable !== undefined &&
                  dataContainerVariable !== null
                  ? dataContainerVariable
                  : ''
              }
            )
          }
        } catch (error) {
          console.error('Error parsing process execution_state:', error)
        }
      }

      return interpolatedUrl
    },
    [flatData, workflow, token, language]
  )

  return interpolateUrl
}

export default useInterpolateUrl
